import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { gatewayApi } from '../api/gatewayApi'
import type { Gateway, GatewayCalibration } from '../types/gateway'

export function useGetGateways(
  params?: { buildingId?: string; floorId?: string; departmentId?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: ['gateways', params],
    queryFn: () => gatewayApi.getGateways(params),
    enabled: enabled,
    retry: false,
    refetchOnWindowFocus: false,
  })
}

export function useGetGateway(id: string, enabled = true) {
  return useQuery({
    queryKey: ['gateway', id],
    queryFn: () => gatewayApi.getGateway(id),
    enabled: enabled && !!id,
  })
}

export function useCreateGateway() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ payload }: { payload: Partial<Gateway> }) => gatewayApi.createGateway(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateways'] })
    },
  })
}

export function useUpdateGateway() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Gateway> }) => gatewayApi.updateGateway(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateways'] })
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
    },
  })
}

export function useDeleteGateway() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => gatewayApi.deleteGateway(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateways'] })
    },
  })
}

export function useUpsertCalibrations() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<GatewayCalibration>[] }) =>
      gatewayApi.upsertCalibrations(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateways'] })
    },
  })
}

export function useDeleteCalibration() {
  return useMutation({
    mutationFn: ({ id, tagId }: { id: string; tagId: string }) => gatewayApi.deleteCalibration(id, tagId),
  })
}
